import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api';
import type { Member, PermissionCapability, Role, WorkspaceResponse } from '../api';
import { ALL_CAPABILITIES, CAPABILITY_LABELS, CAPABILITY_SHORT_LABELS } from '../api';
import { useResource } from '../hooks';
import { ConfirmDialog, ErrorNotice, Field, SkeletonPage, StatusBadge } from '../components/ui';
import { OrganizationAccessTokensSection } from '../components/AccessTokens';
import { Callout, Checkbox, Dialog, Input, Page, PageHeader, Section, Select, Table } from '../components/primitives';

export function TeamPage({ workspace }: { workspace: WorkspaceResponse }) {
  const isOwner = workspace.identity.role?.is_owner_role ?? false;
  // manage_team lets a non-owner invite and remove members; managing roles
  // themselves (create/edit/delete/assign/set default) stays owner-only
  // regardless — Owner is the only built-in role, every other role is
  // entirely owner-created and owner-configured.
  const canManageTeam = isOwner || (workspace.identity.capabilities ?? []).includes('manage_team');

  const members = useResource(() => api.listMembers(), [], undefined, { key: 'team:members' });
  const roles = useResource(() => api.listRoles(), [], undefined, { key: 'team:roles' });
  // useResource keeps the last roles when a reload fails (RoleManager owns
  // unsaved role drafts, so it must stay mounted); the failure surfaces as
  // roles.refreshError below.
  const roleList = roles.data?.roles ?? [];
  // The roles shown are the last ones that loaded, not current. Nothing may be
  // assigned from them (invitation role, member role) until a reload succeeds.
  const rolesStale = Boolean(roles.refreshError);
  // Only someone who can manage the team may list invitations — a plain
  // member fetching this would just get a 403 back, so skip the call
  // entirely instead of surfacing an alarming "forbidden" error for an
  // action never offered.
  const invitations = useResource(
    () => (canManageTeam ? api.listInvitations() : Promise.resolve({ invitations: [] })),
    [canManageTeam], undefined, { key: `team:invitations:${canManageTeam}` },
  );

  const [email, setEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<Error | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [createdInvite, setCreatedInvite] = useState<{ email: string; invite_url?: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  function openInvite() {
    setCreatedInvite(null);
    setFormError(null);
    setInviteOpen(true);
  }

  function closeInvite() {
    setInviteOpen(false);
    setCreatedInvite(null);
    setFormError(null);
  }

  // True whenever this invitation would inherit the organization default
  // (a non-owner never gets a choice; an owner who left the selector on
  // "Organization default" gets it too) — used below to block submitting
  // while a default-role change (handleSetDefaultRole) is still saving, so
  // an invitation can never race a default assignment against what the
  // form is currently showing as that default.
  const inviteUsesDefault = !isOwner || inviteRoleId === '';

  // Derived from the freshly fetched roles list's own is_default flag, not
  // workspace.organization.default_role_id: workspace is fetched once by a
  // distant ancestor (App.tsx's AppGate) and has no reload path from here,
  // so after changing the default (handleSetDefaultRole below) that field
  // would otherwise keep showing the old default until an unrelated full
  // reload — misrepresenting which role a new invitation actually inherits.
  const orgDefaultRole = roleList.find((r) => r.is_default);

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isOwner && rolesStale) return;
    setSubmitting(true);
    setFormError(null);
    setCreatedInvite(null);
    try {
      const created = await api.inviteMember({
        email: email.trim(),
        // An owner who left the selector on "Organization default" gets
        // the exact role this form is currently showing as that default
        // (orgDefaultRole.id), not an unset role_id left for the server to
        // resolve on its own — the two can differ for a moment right after
        // handleSetDefaultRole saves, while this form's own roles fetch is
        // still catching up. Pinning the id actually displayed means the
        // invitation always matches what the owner saw, never a value that
        // changed underneath them between render and submit. A non-owner
        // never gets an explicit choice here, so their invite always
        // leaves role_id unset and simply gets whichever role is the
        // organization's real current default at the moment it is
        // accepted — never this page's own possibly-stale snapshot of it.
        role_id: isOwner ? inviteRoleId || orgDefaultRole?.id : undefined,
      });
      setCreatedInvite({
        email: email.trim(),
        invite_url: created.invite_url,
      });
      setEmail('');
      setInviteRoleId('');
      invitations.reload();
    } catch (cause) {
      setFormError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCopyInviteLink() {
    if (!createdInvite?.invite_url) return;
    try {
      await navigator.clipboard.writeText(createdInvite.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback if clipboard API is restricted
    }
  }

  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [changingRoleUserId, setChangingRoleUserId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<Error | null>(null);

  async function handleRevoke(invitationId: string) {
    setRevokingId(invitationId);
    setMutationError(null);
    try {
      await api.revokeInvitation(invitationId);
      invitations.reload();
    } catch (cause) {
      setMutationError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRevokingId(null);
    }
  }

  async function handleRoleChange(userId: string, roleId: string) {
    setChangingRoleUserId(userId);
    setMutationError(null);
    try {
      await api.updateMemberRole(userId, roleId);
      members.reload();
    } catch (cause) {
      // No reload on failure: the <select> below is controlled directly by
      // members.data (never local state), so it snaps back to the last
      // confirmed role on its own — nothing extra to "retain" here.
      setMutationError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setChangingRoleUserId(null);
    }
  }

  const [pendingRemoval, setPendingRemoval] = useState<Member | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<Error | null>(null);

  async function confirmRemove() {
    if (!pendingRemoval) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await api.removeMember(pendingRemoval.user_id);
      members.reload();
    } catch (cause) {
      // Closes the dialog so the page's own ErrorNotice (behind the modal
      // while it's open) becomes visible again — e.g. "an organization
      // must always have at least one owner" for the last remaining owner.
      setRemoveError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRemoving(false);
      setPendingRemoval(null);
    }
  }

  async function handleSetDefaultRole(roleId: string) {
    setSavingDefault(true);
    setMutationError(null);
    try {
      await api.setDefaultRole(roleId);
      roles.reload();
    } catch (cause) {
      setMutationError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSavingDefault(false);
    }
  }

  // Skeleton only until something has loaded: a reload of a list that is
  // already on screen keeps it mounted instead of flashing to placeholders.
  const loading =
    (members.loading && !members.data) || (invitations.loading && !invitations.data) || roles.loading;

  return (
    <Page>
      <PageHeader
        title="Team"
        actions={
          canManageTeam ? (
            <button type="button" className="button button--primary" onClick={openInvite}>
              Invite teammate
            </button>
          ) : undefined
        }
      />

      {isOwner && rolesStale ? (
        <Callout
          tone="warning"
          title="Roles could not be refreshed"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={roles.reload}>
              Reload roles
            </button>
          }
        >
          Assigning roles is paused until they load again.
        </Callout>
      ) : null}

      {loading ? <SkeletonPage rows={4} /> : null}
      <ErrorNotice error={mutationError ?? removeError ?? members.error ?? invitations.error ?? roles.error ?? roles.refreshError ?? null} />

      {!loading && members.data ? (
        <Section title={`Members (${members.data.members.length})`} testId="team-members">
          {members.data.members.length === 0 ? (
            <p className="quiet-empty">No members</p>
          ) : (
            <Table label="Members" wrapperClassName="table-scroll--shadow-room">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  {isOwner ? (
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {members.data.members.map((member) => (
                  <tr key={member.user_id}>
                    <th scope="row">{member.name}</th>
                    <td className="muted">{member.email}</td>
                    <td>
                      {isOwner ? (
                        <>
                          <Select
                            aria-label={`Role for ${member.name} (${member.email})`}
                            value={member.role_id}
                            onChange={(event) => handleRoleChange(member.user_id, event.target.value)}
                            disabled={changingRoleUserId === member.user_id || rolesStale}
                          >
                            {roleList.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                                {r.is_owner_role ? ' (full access)' : ''}
                              </option>
                            ))}
                          </Select>
                          {changingRoleUserId === member.user_id ? (
                            <span className="muted" role="status">
                              {' '}
                              Saving…
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="badge badge--neutral">{member.role_name}</span>
                      )}
                    </td>
                    {isOwner ? (
                      <td className="team__action">
                        <button
                          className="button button--danger-quiet button--small"
                          type="button"
                          onClick={() => setPendingRemoval(member)}
                          aria-label={`Remove ${member.name}`}
                        >
                          Remove
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Section>
      ) : null}

      {!loading && invitations.data && invitations.data.invitations.length > 0 ? (
        <Section title={`Invitations (${invitations.data.invitations.length})`} testId="team-invitations">
          <Table label="Invitations" wrapperClassName="table-scroll--shadow-room">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                {canManageTeam ? (
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {invitations.data.invitations.map((invitation) => (
                <tr key={invitation.id}>
                  <th scope="row">{invitation.email}</th>
                  <td>
                    <span className="badge badge--neutral">{invitation.role_name}</span>
                  </td>
                  <td>
                    <StatusBadge kind="invitation" value={invitation.status} />
                  </td>
                  {canManageTeam ? (
                    <td className="team__action">
                      {invitation.status === 'pending' ? (
                        <button
                          className="button button--secondary button--small"
                          type="button"
                          onClick={() => handleRevoke(invitation.id)}
                          disabled={revokingId === invitation.id}
                        >
                          {revokingId === invitation.id ? 'Revoking' : 'Revoke'}
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      ) : null}

      {/* Owner only. RoleManager stays mounted while the roles reload, so an
          unsaved role edit survives a refresh (CO-132). */}
      {isOwner ? (
        roles.loading ? (
          <Section>
            <SkeletonPage rows={3} />
          </Section>
        ) : (
          <RoleManager
            roles={roleList}
            refreshing={Boolean(roles.revalidating)}
            onSetDefault={handleSetDefaultRole}
            savingDefault={savingDefault}
            onChange={roles.reload}
          />
        )
      ) : null}

      {isOwner ? <OrganizationAccessTokensSection /> : null}

      <Dialog
        open={inviteOpen}
        size="sm"
        title="Invite a teammate"
        onClose={closeInvite}
        busy={submitting}
        onSubmit={handleInvite}
        testId="team-invite-dialog"
        footer={
          <>
            <button type="button" className="button button--secondary" onClick={closeInvite} disabled={submitting}>
              {createdInvite ? 'Done' : 'Cancel'}
            </button>
            <button
              className="button button--primary"
              type="submit"
              disabled={submitting || !email.trim() || (inviteUsesDefault && savingDefault) || (isOwner && rolesStale)}
            >
              {submitting ? 'Sending' : 'Send invitation'}
            </button>
          </>
        }
      >
        {createdInvite ? (
          <Callout
            tone="success"
            title={createdInvite.invite_url ? `Invitation created for ${createdInvite.email}` : `Invitation emailed to ${createdInvite.email}`}
            actions={
              createdInvite.invite_url ? (
                <div className="copy-field">
                  <Input
                    type="text"
                    readOnly
                    value={createdInvite.invite_url}
                    aria-label="Invitation link URL"
                    className="input--mono"
                    onFocus={(e) => e.target.select()}
                  />
                  <button type="button" className="button button--secondary" onClick={handleCopyInviteLink} aria-label="Copy invitation link">
                    {copied ? 'Copied' : 'Copy link'}
                  </button>
                </div>
              ) : undefined
            }
          />
        ) : null}
        <Field label="Email">
          <Input required data-autofocus type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="teammate@example.com" />
        </Field>
        {isOwner ? (
          <Field label="Role" hint={orgDefaultRole ? `Default: ${orgDefaultRole.name}` : undefined}>
            <Select value={inviteRoleId} onChange={(event) => setInviteRoleId(event.target.value)} disabled={rolesStale}>
              <option value="">Organization default</option>
              {roleList.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.is_owner_role ? ' (full access)' : ''}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <p className="muted">Role: organization default{orgDefaultRole ? ` (${orgDefaultRole.name})` : ''}</p>
        )}
        <ErrorNotice error={formError} />
        {inviteUsesDefault && savingDefault ? (
          <p className="muted" role="status">
            Saving the organization default…
          </p>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Remove this member?"
        description={
          pendingRemoval
            ? `${pendingRemoval.name} (${pendingRemoval.email}) will immediately lose access to ${workspace.organization.name}.`
            : ''
        }
        confirmLabel="Remove member"
        danger
        busy={removing}
        onConfirm={confirmRemove}
        onCancel={() => setPendingRemoval(null)}
      />
    </Page>
  );
}

/** RoleManager is the owner-only card for creating, editing, deleting, and
 * choosing the default among an organization's roles. Owner is the only
 * built-in role — it always appears in the list with full access, but has
 * no edit/delete/default controls: it cannot be modified. */
function RoleManager({
  roles,
  refreshing,
  onSetDefault,
  savingDefault,
  onChange,
}: {
  roles: Role[];
  refreshing: boolean;
  onSetDefault: (roleId: string) => void;
  savingDefault: boolean;
  onChange: () => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteRole(pendingDelete.id);
      onChange();
    } catch (cause) {
      // Closes the dialog so the card's own ErrorNotice (behind the modal
      // while it's open) becomes visible again, surfacing exactly why the
      // deletion was refused — "in use" or "is the default".
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }

  return (
    <Section
      title={`Roles (${roles.length})`}
      testId="team-roles"
      actions={
        creating ? undefined : (
          <button className="button button--secondary button--small" type="button" onClick={() => setCreating(true)}>
            New role
          </button>
        )
      }
    >
      <div aria-busy={refreshing}>
      <ErrorNotice error={error} />
      <Table label="Roles" wrapperClassName="table-scroll--shadow-room">
        <thead>
          <tr>
            <th>Name</th>
            <th>Capabilities</th>
            <th>Default</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {roles.map((r) =>
            r.is_owner_role ? (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td className="muted">Every capability</td>
                <td>
                  <span className="badge">Full access · built-in</span>
                </td>
                <td />
              </tr>
            ) : editingId === r.id ? (
              <RoleEditRow
                key={r.id}
                role={r}
                onDone={() => {
                  setEditingId(null);
                  onChange();
                }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>
                  {r.capabilities.length > 0 ? (
                    r.capabilities.map((capability) => CAPABILITY_SHORT_LABELS[capability] ?? capability).join(', ')
                  ) : (
                    <span className="muted">No capabilities</span>
                  )}
                </td>
                <td>
                  {r.is_default ? (
                    <span className="badge">Default</span>
                  ) : (
                    <button
                      className="link-button"
                      type="button"
                      onClick={() => onSetDefault(r.id)}
                      disabled={savingDefault}
                    >
                      Set as default
                    </button>
                  )}
                </td>
                <td>
                  <button className="link-button" type="button" onClick={() => setEditingId(r.id)}>
                    Edit
                  </button>{' '}
                  <button className="link-button" type="button" onClick={() => setPendingDelete(r)}>
                    Delete
                  </button>
                </td>
              </tr>
            ),
          )}
        </tbody>
      </Table>

      {creating ? (
        <RoleCreateForm
          onDone={() => {
            setCreating(false);
            onChange();
          }}
          onCancel={() => setCreating(false)}
        />
      ) : null}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this role?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" will be deleted. This is refused if it's still the organization default or assigned to any member or pending invitation.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Section>
  );
}

function CapabilityCheckboxes({
  selected,
  onToggle,
}: {
  selected: Set<PermissionCapability>;
  onToggle: (capability: PermissionCapability, checked: boolean) => void;
}) {
  return (
    <div className="checkbox-group">
      {ALL_CAPABILITIES.map((capability) => (
        <Checkbox
          key={capability}
          checked={selected.has(capability)}
          onChange={(event) => onToggle(capability, event.target.checked)}
          label={<>{CAPABILITY_LABELS[capability]}</>}
        />
      ))}
    </div>
  );
}

function RoleCreateForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [capabilities, setCapabilities] = useState<Set<PermissionCapability>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.createRole({ name: name.trim(), capabilities: Array.from(capabilities) });
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <Field label="Name">
        <Input required value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Deploy only" />
      </Field>
      <CapabilityCheckboxes
        selected={capabilities}
        onToggle={(capability, checked) =>
          setCapabilities((current) => {
            const next = new Set(current);
            if (checked) next.add(capability);
            else next.delete(capability);
            return next;
          })
        }
      />
      <ErrorNotice error={error} />
      <div className="inline-form">
        <button className="button" type="submit" disabled={submitting || !name.trim()}>
          {submitting ? 'Creating' : 'Create'}
        </button>
        <button className="button button--secondary" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RoleEditRow({
  role,
  onDone,
  onCancel,
}: {
  role: Role;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(role.name);
  const [capabilities, setCapabilities] = useState<Set<PermissionCapability>>(new Set(role.capabilities));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSave() {
    setSubmitting(true);
    setError(null);
    try {
      await api.updateRole(role.id, { name: name.trim(), capabilities: Array.from(capabilities) });
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      setSubmitting(false);
    }
  }

  return (
    <tr>
      <td colSpan={4}>
        <div className="form">
          <Field label="Name">
            <Input required value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <CapabilityCheckboxes
            selected={capabilities}
            onToggle={(capability, checked) =>
              setCapabilities((current) => {
                const next = new Set(current);
                if (checked) next.add(capability);
                else next.delete(capability);
                return next;
              })
            }
          />
          <ErrorNotice error={error} />
          <div className="inline-form">
            <button className="button" type="button" onClick={handleSave} disabled={submitting || !name.trim()}>
              {submitting ? 'Saving' : 'Save'}
            </button>
            <button className="button button--secondary" type="button" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}
